import React from "react";
import Select, { components } from "react-select";

export const normalizeAdminNodeCodes = (nodeValue) => {
  const values = Array.isArray(nodeValue) ? nodeValue : [nodeValue];

  return values.reduce((codes, value) => {
    if (value === undefined || value === null) {
      return codes;
    }

    const code = String(value).trim();
    if (code && !codes.includes(code)) {
      codes.push(code);
    }

    return codes;
  }, []);
};

export const buildAdminNodeOptions = (nodesList, selectedNodeCodes) => {
  const seenCodes = new Set();
  const options = (nodesList || [])
    .reduce((nodeOptions, node) => {
      const code = node && node.nodecode ? String(node.nodecode).trim() : "";
      if (!code || seenCodes.has(code)) {
        return nodeOptions;
      }

      const name = node.nodename ? String(node.nodename).trim() : "";
      seenCodes.add(code);
      nodeOptions.push({
        value: code,
        label: name ? `${name} (${code})` : code,
        nodeName: name,
        nodeCode: code,
      });
      return nodeOptions;
    }, [])
    .sort((first, second) =>
      first.label.localeCompare(second.label, "th", { sensitivity: "base" })
    );

  normalizeAdminNodeCodes(selectedNodeCodes).forEach((code) => {
    if (!seenCodes.has(code)) {
      options.push({
        value: code,
        label: `${code} (saved node)`,
        nodeName: "",
        nodeCode: code,
        isSavedOnly: true,
      });
    }
  });

  return options;
};

const NodeOption = (props) => (
  <components.Option {...props}>
    <div className="d-flex align-items-center">
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        tabIndex="-1"
        aria-hidden="true"
        className="mr-2"
        style={{ pointerEvents: "none" }}
      />
      <div>
        <div>{props.data.nodeName || props.data.nodeCode}</div>
        {props.data.nodeName && (
          <small className="text-muted">Node code: {props.data.nodeCode}</small>
        )}
        {props.data.isSavedOnly && (
          <small className="text-warning d-block">
            Saved on this admin but not found in the current node list
          </small>
        )}
      </div>
    </div>
  </components.Option>
);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "46px",
    borderColor: state.isFocused ? "#593bdb" : "#d7dae3",
    boxShadow: state.isFocused ? "0 0 0 1px #593bdb" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#593bdb" : "#b5b9c5",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "4px 8px",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#eeebff",
    borderRadius: "4px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#3e2a8a",
    fontSize: "13px",
    padding: "5px 4px 5px 8px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#6c55c7",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#dc3545",
      color: "white",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 1065,
  }),
  option: (base) => ({
    ...base,
    cursor: "pointer",
  }),
};

const AdminNodeSelect = ({
  nodesList,
  value,
  onChange,
  inputId,
  placeholder = "Search node name or code...",
}) => {
  const selectedCodes = normalizeAdminNodeCodes(value);
  const options = buildAdminNodeOptions(nodesList, selectedCodes);
  const selectedOptions = selectedCodes
    .map((code) => options.find((option) => option.value === code))
    .filter(Boolean);

  return (
    <div>
      <Select
        inputId={inputId}
        aria-label="Select nodes"
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        blurInputOnSelect={false}
        menuPlacement="auto"
        components={{ Option: NodeOption }}
        styles={selectStyles}
        options={options}
        value={selectedOptions}
        placeholder={placeholder}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "No matching nodes" : "No nodes available"
        }
        onChange={(selected) =>
          onChange((selected || []).map((option) => option.value))
        }
      />
      <div className="d-flex flex-wrap justify-content-between align-items-center mt-1">
        <small className="text-muted mr-2">
          {selectedCodes.length
            ? `${selectedCodes.length} node${
                selectedCodes.length === 1 ? "" : "s"
              } selected. Use × to remove a node.`
            : "Search by full node name or short code, then click to add."}
        </small>
        {selectedCodes.length > 0 && (
          <button
            type="button"
            className="btn btn-link btn-sm text-danger p-0"
            onClick={() => onChange([])}
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminNodeSelect;
